import { useAuth } from "@elit-hair/elit-tools";
import { Badge, Button, EmptyState, ErrorState, LoadingState } from "@elit-hair/elit-ui";
import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { TbBrain, TbChartBar, TbMessageStar, TbPlus, TbSettings } from "react-icons/tb";
import { toast } from "sonner";

import { PERMISSIONS } from "@/constants/permissions";
import { FeedbackDialog, MemoryDialog, SettingsDialog, UsageDialog } from "@/pages/feature-videos/components/dialogs";
import { JobDetail } from "@/pages/feature-videos/components/job-detail";
import { NewVideoDialog } from "@/pages/feature-videos/components/new-video-dialog";
import { fmtTime, fmtUsd } from "@/pages/feature-videos/utils/fv.utils";
import { useFvT } from "@/pages/feature-videos/utils/use-fv-t";
import { fvErrorMessage, getFvJobContext, resolveFvJobName, useFvConfig, useFvJobs, useFvMe, useFvStatus } from "@/queries/feature-videos.queries";
import { FvForm, FvJob } from "@/types/feature-videos.types";
import { APP_ROUTES_FULL, APP_ROUTES_TO } from "@/utils/routes";
import { FV_ROUTE_PERMISSIONS, fvCan } from "@/pages/feature-videos/constants";

export const Route = createFileRoute(APP_ROUTES_FULL.FEATURE_VIDEOS)({
    component: FeatureVideosPage,
    staticData: {
        config: {
            breadcrumb: { title: "feature_videos", to: APP_ROUTES_TO.FEATURE_VIDEOS },
            title: "feature_videos",
        },
        permissions: FV_ROUTE_PERMISSIONS,
    },
});

type DialogName = "new" | "usage" | "memory" | "feedback" | "settings" | null;

function JobRow({ job, active, onClick }: { job: FvJob; active: boolean; onClick: () => void }) {
    const tt = useFvT();
    const running = job.status === "running";
    return (
        <div className={`relative rounded-lg ${running ? "p-0.5" : ""}`}>
            {running && (
                <span aria-hidden className="pointer-events-none absolute inset-0 overflow-hidden rounded-lg">
                    <span className="absolute -inset-full flex items-center justify-center">
                        <span className="aspect-square w-full animate-spin bg-[conic-gradient(from_0deg,transparent_55%,var(--primary)_100%)]" />
                    </span>
                </span>
            )}
            <button
                type="button"
                onClick={onClick}
                className={`relative z-10 flex w-full flex-col gap-1 border p-2.5 text-left transition-colors ${
                    running
                        ? `rounded-[6px] border-transparent ${active ? "bg-muted" : "bg-background hover:bg-muted"}`
                        : `rounded-lg ${active ? "border-primary bg-primary/5" : "border-border hover:bg-muted/50"}`
                }`}
            >
            <span className="line-clamp-2 text-sm font-medium">{job.name}</span>
            <span className="flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground">
                <Badge variant={running ? "info" : job.status === "terminated" ? "neutral" : "success"} size="xs">
                    {running ? tt("running", "running") : job.status}
                </Badge>
                {fmtTime(job.createdAt)}
                {job.usage?.cost != null && <span>· {fmtUsd(job.usage.cost)}</span>}
                {job.feedback && (
                    <span className={job.feedback === "good" ? "font-semibold text-emerald-600" : "font-semibold text-red-600"}>
                        {job.feedback === "good" ? "✓" : "✗"}
                    </span>
                )}
            </span>
            <span className="flex justify-between text-[11px] text-muted-foreground">
                <span>{job.ownerName}</span>
                <span>{job.ref}</span>
            </span>
            </button>
        </div>
    );
}

function FeatureVideosPage() {
    const tt = useFvT();
    const { userData } = useAuth();
    const codes: string[] = userData?.permissionCodes || [];
    const canCreate = fvCan(codes, PERMISSIONS.MISC_FEATURE_VIDEOS_CREATE);

    const { data: config, isLoading: configLoading, error: configError, refetch: refetchConfig } = useFvConfig();
    const { data: status, error: statusError } = useFvStatus(!!config?.enabled);
    const { data: me } = useFvMe();
    const [mine, setMine] = useState(false);
    const { data: jobs = [], isLoading: jobsLoading } = useFvJobs(mine, !!config?.enabled);
    const [currentId, setCurrentId] = useState<string | null>(null);
    const [dialog, setDialog] = useState<DialogName>(null);
    const [initial, setInitial] = useState<Partial<FvForm> | null>(null);
    const naming = useRef(new Set<string>());

    const current = jobs.find((j) => j.id === currentId) || null;

    useEffect(() => {
        if (!currentId && jobs[0]) setCurrentId(jobs[0].id);
    }, [jobs, currentId]);

    // Older jobs without an AI-written title get one (once).
    useEffect(() => {
        for (const j of jobs) {
            if (j.needsName && Date.now() - new Date(j.createdAt).getTime() > 60_000 && !naming.current.has(j.id)) {
                naming.current.add(j.id);
                resolveFvJobName(j.id).catch(() => undefined);
            }
        }
    }, [jobs]);

    // Browser notification when one of my jobs stops (if allowed).
    const prevStatus = useRef(new Map<string, string>());
    useEffect(() => {
        for (const j of jobs) {
            const before = prevStatus.current.get(j.id);
            if (before === "running" && j.status !== "running" && j.ownerId === userData?.id) {
                toast.success(tt("job_stopped", "“{{name}}” stopped — check the result.", { name: j.name }));
                if ("Notification" in window && Notification.permission === "granted") new Notification(j.name, { body: tt("job_stopped_short", "The video job stopped.") });
            }
            prevStatus.current.set(j.id, j.status);
        }
    }, [jobs]);

    const openNew = (form: Partial<FvForm> | null) => {
        if (!me?.hasAppLogin) {
            toast.info(tt("login_first", "Add your Elitphone login first."));
            setDialog("settings");
            return;
        }
        if ("Notification" in window && Notification.permission === "default") Notification.requestPermission().catch(() => undefined);
        setInitial(form);
        setDialog("new");
    };

    const duplicate = async (job: FvJob) => {
        try {
            openNew(await getFvJobContext(job.id));
        } catch (err) {
            toast.error(fvErrorMessage(err));
        }
    };

    if (configLoading) return <LoadingState />;
    if (configError) return <ErrorState title={tt("load_failed", "Could not load feature videos")} message={fvErrorMessage(configError)} onRetry={() => refetchConfig()} />;
    if (!config?.enabled)
        return (
            <EmptyState
                variant="card"
                title={tt("not_configured", "Feature videos are not configured")}
                description={tt("not_configured_desc", "Set FEATURE_VIDEO_ANTHROPIC_API_KEY in misc-svc to enable this section.")}
            />
        );

    return (
        <div className="flex flex-col gap-4 p-4">
            <div className="flex flex-wrap items-center gap-2">
                <div className="mr-auto">
                    <h1 className="text-xl font-semibold">{tt("title", "Feature videos")}</h1>
                    <p className="text-sm text-muted-foreground">
                        {tt("subtitle", "Give the agent a GitHub task; it records the feature in {{app}} and renders a demo video.", { app: new URL(config.appUrl).hostname })}
                        {statusError ? <span className="ml-2 text-destructive">{fvErrorMessage(statusError)}</span> : !status ? <span className="ml-2">{tt("preparing", "Preparing the agent…")}</span> : null}
                    </p>
                </div>
                <Button variant="outline" size="sm" onClick={() => setDialog("usage")}>
                    <TbChartBar /> {tt("usage", "Usage")}
                </Button>
                <Button variant="outline" size="sm" onClick={() => setDialog("feedback")}>
                    <TbMessageStar /> {tt("feedback", "Feedback")}
                </Button>
                <Button variant="outline" size="sm" onClick={() => setDialog("memory")}>
                    <TbBrain /> {tt("memory", "Memory")}
                </Button>
                <Button variant="outline" size="sm" onClick={() => setDialog("settings")}>
                    <TbSettings /> {tt("settings", "Settings")}
                </Button>
                {canCreate && (
                    <Button size="sm" onClick={() => openNew(null)}>
                        <TbPlus /> {tt("new_video", "New video")}
                    </Button>
                )}
            </div>

            <div className="grid grid-cols-1 gap-4 lg:grid-cols-[320px_1fr]">
                <div className="flex flex-col gap-2">
                    <div className="flex gap-1">
                        <Button size="sm" variant={mine ? "ghost" : "secondary"} onClick={() => setMine(false)}>
                            {tt("everyone", "Everyone")}
                        </Button>
                        <Button size="sm" variant={mine ? "secondary" : "ghost"} onClick={() => setMine(true)}>
                            {tt("mine", "Mine")}
                        </Button>
                    </div>
                    {jobsLoading ? (
                        <LoadingState />
                    ) : !jobs.length ? (
                        <EmptyState variant="simple" title={tt("no_jobs", "No videos yet")} description={tt("no_jobs_desc", "Create the first one with New video.")} />
                    ) : (
                        <div className="flex max-h-[75vh] flex-col gap-1.5 overflow-y-auto pb-px pr-1">
                            {jobs.map((j) => (
                                <JobRow key={j.id} job={j} active={j.id === currentId} onClick={() => setCurrentId(j.id)} />
                            ))}
                        </div>
                    )}
                </div>
                <div className="min-w-0 rounded-xl border border-border p-4">
                    {current ? (
                        <JobDetail key={current.id} job={current} feedbackTags={config.feedbackTags} onDuplicate={() => duplicate(current)} />
                    ) : (
                        <p className="text-sm text-muted-foreground">{tt("select_job", "Select or create a video job to follow the agent live.")}</p>
                    )}
                </div>
            </div>

            <NewVideoDialog
                open={dialog === "new"}
                onClose={() => setDialog(null)}
                config={config}
                status={status}
                initial={initial}
                onCreated={(job) => {
                    setMine(false);
                    setCurrentId(job.id);
                }}
            />
            <UsageDialog open={dialog === "usage"} onClose={() => setDialog(null)} onOpenJob={(j) => setCurrentId(j.id)} />
            <FeedbackDialog open={dialog === "feedback"} onClose={() => setDialog(null)} />
            <MemoryDialog open={dialog === "memory"} onClose={() => setDialog(null)} />
            <SettingsDialog open={dialog === "settings"} onClose={() => setDialog(null)} />
        </div>
    );
}
